import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { knowledgeService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Eye, Trash2, Loader2 } from "lucide-react";
import { KB_STATUSES, VISIBILITIES, STATUS_COLOR, VISIBILITY_COLOR } from "./constants";

const EMPTY = { title: "", summary: "", visibility: "Internal", categoryId: "", tenantId: "" };

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-foreground mb-1">{label}</label>{children}</div>;
}

export default function ArticlesPage() {
  const { hasPermission, user } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [visFilter, setVisFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [categories, setCategories] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await knowledgeService.listArticles({ status: statusFilter || undefined, visibility: visFilter || undefined, size: 200 });
      setRows(d.items || []);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [statusFilter, visFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);
  useEffect(() => {
    const tid = isSuper ? form.tenantId : undefined;
    if (isSuper && !tid) { setCategories([]); return; }
    knowledgeService.listCategories({ tenantId: tid }).then((d) => setCategories(d.items || [])).catch(() => {});
  }, [isSuper, form.tenantId]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (isSuper && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const payload = { ...form, categoryId: form.categoryId || null };
      if (!isSuper) delete payload.tenantId;
      const a = await knowledgeService.createArticle(payload);
      toast.success("Article created");
      setOpen(false); navigate(`/admin/knowledge/articles/${a.id}`);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete article "${r.title}"?`)) return;
    try { await knowledgeService.removeArticle(r.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const canCreate = hasPermission("KNOWLEDGE.CREATE");
  const canDelete = hasPermission("KNOWLEDGE.DELETE");

  const columns = [
    { key: "title", header: "Article", render: (r) => <span className="font-medium text-foreground">{r.title}</span> },
    { key: "status", header: "Status", render: (r) => <Badge color={STATUS_COLOR[r.status] || "slate"}>{r.status}</Badge> },
    { key: "visibility", header: "Visibility", render: (r) => <Badge color={VISIBILITY_COLOR[r.visibility] || "slate"}>{r.visibility}</Badge> },
    { key: "version", header: "Ver", render: (r) => <span className="text-muted-foreground">v{r.currentVersion ?? 1}</span> },
    { key: "views", header: "Views", render: (r) => <span className="text-foreground">{r.viewCount ?? 0}</span> },
    {
      key: "_a", header: "",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => navigate(`/admin/knowledge/articles/${r.id}`)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`article-view-${r.id}`}><Eye className="h-4 w-4" /></button>
          {canDelete && <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`article-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="articles-page">
      <PageHeader title="Articles" subtitle="Versioned, approval-gated knowledge articles"
        actions={canCreate && (
          <button onClick={() => { setForm(EMPTY); setOpen(true); }} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="article-create-btn">
            <Plus className="h-4 w-4" /> New Article
          </button>
        )}
      />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="article-status-filter">
          <option value="">All statuses</option>
          {KB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={visFilter} onChange={(e) => setVisFilter(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="article-visibility-filter">
          <option value="">All visibility</option>
          {VISIBILITIES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <DataTable columns={columns} rows={rows} loading={loading} testid="articles-table" empty="No articles yet" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="article-dialog">
            <h3 className="text-lg font-bold mb-4">New Article</h3>
            <div className="space-y-3.5">
              {isSuper && (
                <Field label="Tenant *">
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value, categoryId: "" })} className="ciq-input" data-testid="article-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Title *"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="ciq-input" data-testid="article-title" /></Field>
              <Field label="Summary"><textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="ciq-input" rows={2} data-testid="article-summary" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Visibility"><select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} className="ciq-input" data-testid="article-visibility">{VISIBILITIES.map((v) => <option key={v}>{v}</option>)}</select></Field>
                <Field label="Category"><select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })} className="ciq-input" data-testid="article-category"><option value="">None</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="article-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create &amp; Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
